import React, { useContext, useState, useCallback } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import PhoneInput from 'react-phone-number-input';
import metadata from 'libphonenumber-js/metadata.min.json';

import CloseIcon from '@material-ui/icons/Delete';
import { grey } from '@material-ui/core/colors';
import shortid from 'shortid';
import BookingNav from './BookingNav';
import BrandFooter from './BrandFooter';
import { PageContext } from '../context';

import { FlexFlow } from '../../../components/styles/Layout';
import Divider from '../../../components/Divider/Divider';

import ConfirmationDialog from '../../../components/Dialogs/ConfirmationDialog';

import {
  StyledIconButton,
  ColorButton,
  CssTextField,
  PhoneInputWrapper,
} from '../../../components/Forms/styles';
import { CardTextArea, LPText } from '../../styles';
import { ContentWrapper } from '../styles';

const BookingGuests = () => {
  const context = useContext(PageContext);
  const {
    setField,
    updateUsedFields,
    hasErrorMessage,
    isFormValid,
    formData: {
      firstName, lastName, primaryPhoneNumber, description,
    },
    queryData: {
      getVpn: {
        bookingLandingPage: { guestFormInstr },
      },
    },
  } = context;

  const [guests, setGuests] = useState([]);
  const [modalData, setModalData] = useState({
    showActionBtns: true,
    title: 'Confirm',
    confirmBtnCopy: 'Delete',
    open: false,
    handleCancel: () => setModalData({ ...modalData, open: false }),
    handleConfirm: () => {},
    isValid: true,
  });

  const guestData = {
    id: '',
    guestFirstName: '',
    guestLastName: '',
    guestPhone: '',
  };

  const handleChange = (value, name) => {
    setField(name, value);
  };

  const handleOnBlur = (name) => {
    updateUsedFields(name);
  };

  const addGuest = useCallback(() => {
    const newGuest = guestData;
    newGuest.id = shortid.generate();
    setGuests([...guests, newGuest]);
  }, [guests]);

  const removeGuest = (guestId) => {
    const newGuests = guests.filter((g) => g.id !== guestId);
    setGuests(newGuests);
  };

  const handleRemoveGuest = (guestId) => {
    setModalData({
      ...modalData,
      handleConfirm: () => {
        removeGuest(guestId);
        setModalData({ ...modalData, open: false });
      },
      open: true,
    });
  };

  const setGuestField = useCallback(
    (id, name, value) => {
      setGuests(guests.map((g) => (id === g.id ? { ...g, [name]: value } : { ...g })));
    },
    [guests],
  );

  return (
    <>
      <ContentWrapper>
        <LPText padded w800>
          {guestFormInstr}
        </LPText>

        <BookingNav />
        <ContentWrapper nopadding>
          <FlexFlow column splitSpace="1em" padding="1em">
            <FlexFlow nowrap splitSpace="1em">
              <CssTextField
                fullWidth
                margin="dense"
                variant="outlined"
                label="First Name"
                value={firstName}
                onChange={(e) => handleChange(e.target.value, 'firstName')}
                onBlur={() => handleOnBlur('firstName')}
                error={hasErrorMessage('firstName').length > 0}
                helperText={
                  hasErrorMessage('firstName').length > 0
                    ? hasErrorMessage('firstName').join(' | ')
                    : ''
                }
              />
              <CssTextField
                fullWidth
                margin="dense"
                variant="outlined"
                label="Last Name"
                value={lastName}
                onChange={(e) => handleChange(e.target.value, 'lastName')}
                onBlur={() => handleOnBlur('lastName')}
                error={hasErrorMessage('lastName').length > 0}
                helperText={
                  hasErrorMessage('lastName').length > 0
                    ? hasErrorMessage('lastName').join(' | ')
                    : ''
                }
              />
            </FlexFlow>
            <CardTextArea>
              <PhoneInputWrapper>
                <PhoneInput
                  defaultCountry="US"
                  label="Phone"
                  metadata={metadata}
                  placeholder="Mobile # (w/country code)"
                  value={primaryPhoneNumber}
                  onChange={(value) => handleChange(value, 'primaryPhoneNumber')}
                  onBlur={() => handleOnBlur('primaryPhoneNumber')}
                />
              </PhoneInputWrapper>

              {hasErrorMessage('primaryPhoneNumber').length > 0 && (
                <LPText small error clear nomargin>
                  {hasErrorMessage('primaryPhoneNumber').join(' | ')}
                </LPText>
              )}
            </CardTextArea>

            <div className="sc-hide">
              {guests.map(({
                id, guestFirstName, guestLastName, guestPhone,
              }, ind) => (
                <FlexFlow key={id} column>
                  <FlexFlow splitSpace="1em" nowrap grow>
                    <FlexFlow splitSpace="1em" nowrap grow>
                      <Divider label={`Guest #${ind + 1}`} />
                    </FlexFlow>
                    <StyledIconButton aria-label="delete" onClick={() => handleRemoveGuest(id)}>
                      <CloseIcon />
                    </StyledIconButton>
                  </FlexFlow>
                  <FlexFlow nowrap splitSpace="1em">
                    <CssTextField
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      label="First Name"
                      value={guestFirstName}
                      onChange={(e) => setGuestField(id, 'guestFirstName', e.target.value)}
                    />
                    <CssTextField
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      label="Last Name"
                      value={guestLastName}
                      onChange={(e) => setGuestField(id, 'guestLastName', e.target.value)}
                    />
                  </FlexFlow>
                  <CardTextArea>
                    <PhoneInputWrapper>
                      <PhoneInput
                        defaultCountry="US"
                        label="Phone"
                        metadata={metadata}
                        placeholder="Mobile # (w/country code)"
                        value={guestPhone}
                        onChange={(value) => setGuestField(id, 'guestPhone', value)}
                      />
                    </PhoneInputWrapper>
                  </CardTextArea>
                </FlexFlow>
              ))}
              <ColorButton
                size="small"
                variant="outlined"
                bgcolor="var(--brand-color)"
                textColor={grey[50]}
                onClick={() => addGuest()}
              >
                <FiPlusCircle />
                &nbsp; Add Guest
              </ColorButton>
            </div>

            <Divider />

            <CssTextField
              fullWidth
              variant="outlined"
              multiline
              rows="4"
              label="Enter Text..."
              value={description}
              onChange={(e) => handleChange(e.target.value, 'description')}
              margin="dense"
            />
          </FlexFlow>
        </ContentWrapper>
      </ContentWrapper>

      <BrandFooter disabled={!isFormValid} />

      <ConfirmationDialog {...modalData}>Delete this guest?</ConfirmationDialog>
    </>
  );
};

BookingGuests.propTypes = {};

export default BookingGuests;
